// src/features/rrhh/mensajeria/pages/MensajesPage.jsx
import { useEffect, useCallback, useMemo, useState } from "react";
import { Alert, Box, IconButton, Snackbar, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

import MensajesFilters from "../components/MensajesFilters";
import MensajesList from "../components/MensajesList";
import MensajeForm from "../components/MensajeForm";
import MensajesSearchPanel from "../components/MensajesSearchPanel";
import MensajeDetailModal from "../components/MensajeDetailModal";
import DeleteMessageModal from "../components/DeleteMessageModal";

import ChatSidebarEmpleados from "../components/ChatSidebarEmpleados";
import ChatThreadDrawer from "../components/ChatThreadDrawer";

import ChatModeSwitch from "../components/ChatModeSwitch";
import ChatSidebarGrupos from "../components/ChatSidebarGrupos";

import ReclamosFilters from "../components/ReclamosFilters";
import ReclamosTable from "../components/ReclamosTable";
import ReclamoDetailModal from "../components/ReclamoDetailModal";

import { 
  listarSucursales, 
  listarMensajes, 
  crearMensaje,
  eliminarMensaje,
} from "../api/mensajesApi"

import {
  listarChatEmpleados,
  crearChatHilo,
  listarMensajesChat,
  enviarMensajeChat,
  marcarChatLeidoWeb,
} from "../api/chatApi";

import {
  listarGruposAreaChat,
  listarMensajesGrupoArea,
  enviarMensajeGrupoArea,
} from "../api/areaChatApi";

import {
  listarReclamos,
  cambiarEstadoReclamo,
  responderReclamo,
} from "../api/reclamosApi"


function MainPill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 4,
        py: 1,
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: (t) => t.palette?.secondary?.[200] || "secondary.200",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.45)",
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900", lineHeight: 1 }}>
        {children}
      </Typography>
    </Box>
  );
}

function SectionPill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        mb: 1,
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: (t) => t.palette?.secondary?.[50] || "secondary.200",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.45)",
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "primary.900", lineHeight: 1 }}>
        {children}
      </Typography>
    </Box>
  );
}

function OuterSurface({ children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "30px",
        bgcolor: (t) => t.palette?.neutral?.[300] || "rgba(165,165,178,1)",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.45)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ✅ Superficie interna tipo “Ultimos mensajes” (gris más claro)
function InnerPanel({ children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "20px",
        bgcolor: (t) => t.palette?.neutral?.[150],
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.45)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const TEMA_TO_API = {
  "General": "GENERAL",
  "Novedades": "NOVEDADES",
  "Eventos": "EVENTOS",
  "Capacitaciones": "CAPACITACIONES",
  "Directivas": "DIRECTIVAS",
  "Calendario Especial": "CALENDARIO_ESPECIAL",
  "Otro": "OTRO",
};

const TEMA_FROM_API = {
  GENERAL: "General",
  NOVEDADES: "Novedades",
  EVENTOS: "Eventos",
  CAPACITACIONES: "Capacitaciones",
  DIRECTIVAS: "Directivas",
  CALENDARIO_ESPECIAL: "Calendario Especial",
  OTRO: "Otro",
};

const ALCANCE_TO_API = { Empresa: "EMPRESA", Sucursal: "SUCURSAL" };
const ALCANCE_FROM_API = { EMPRESA: "Empresa", SUCURSAL: "Sucursal" };

// -------- RECLAMOS (BACKEND) --------
const PRIORIDAD_FROM_API = { BAJA: "Baja", MEDIA: "Media", ALTA: "Alta" };

const ESTADO_FROM_API = {
  NUEVO: "Nuevo",
  EN_REVISION: "En revisión",
  RESPONDIDO: "Respondido",
  CERRADO: "Cerrado",
};

function fmtFechaAR(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : String(iso);
}

function makePreview(txt, n = 90) {
  const s = String(txt || "").trim();
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function toUiMessage(apiMsg) {
  const title = apiMsg.title ?? apiMsg.titulo ?? "—";
  const topic = apiMsg.topic ?? apiMsg.tema ?? "OTRO";
  const scope = apiMsg.scope ?? apiMsg.alcance ?? "EMPRESA";
  const content = apiMsg.content ?? apiMsg.contenido ?? "";
  const publishedAt = apiMsg.publishedAt ?? apiMsg.fecha_publicacion ?? null;

  return {
    id: apiMsg.id,
    titulo: title,
    tema: TEMA_FROM_API[String(topic)] || String(topic || "Otro"),
    alcance: ALCANCE_FROM_API[String(scope)] || String(scope || "Empresa"),
    preview: makePreview(content),
    fecha: fmtFechaAR(publishedAt),
    _raw: apiMsg,
  };
}

function fmtHoraAR(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function getCurrentUserIdFromStorage() {
  try {
    const raw = localStorage.getItem("sabi_user");
    if (!raw) return null;

    const user = JSON.parse(raw);
    const id = Number(user?.id ?? user?.userId);

    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

function toUiEmpleadoChat(apiRow) {
  const employeeId = apiRow.employeeId ?? apiRow.empleado_id;
  const userId = apiRow.userId ?? apiRow.empleado_user_id ?? null;

  const areas = Array.isArray(apiRow.areas) ? apiRow.areas : [];
  const branches = Array.isArray(apiRow.branches) ? apiRow.branches : [];

  return {
    id: employeeId,
    employeeId,
    empleado_id: employeeId,

    userId,
    empleado_user_id: userId,

    nombre: apiRow.fullName ?? apiRow.nombre ?? `Empleado #${employeeId}`,

    area:
      apiRow.area ??
      apiRow.areasLabel ??
      (areas.length ? areas.join(", ") : null),

    sucursal:
      apiRow.sucursal ??
      apiRow.branchesLabel ??
      (branches.length ? branches.join(", ") : null),

    foto_url: null,
    profilePhotoId: apiRow.profilePhotoId ?? apiRow.foto_url ?? null,

    estado_empleado: apiRow.status ?? apiRow.estado_empleado ?? "ACTIVE",

    hilo_id: apiRow.threadId ?? apiRow.hilo_id ?? null,
    chat_estado: apiRow.chatState ?? apiRow.chat_estado ?? null,
    fecha_ultimo_mensaje: apiRow.lastMessageAt ?? apiRow.fecha_ultimo_mensaje ?? null,
    ultimo_mensaje_id: apiRow.lastMessageId ?? apiRow.ultimo_mensaje_id ?? null,
    ultimo_mensaje_autor:
      apiRow.lastMessageAuthorType ?? apiRow.ultimo_mensaje_autor ?? null,
    unreadForRrhh:
      Boolean(apiRow.unreadForRrhh) ||
      Boolean(apiRow.unread_for_rrhh) ||
      Boolean(apiRow.no_leido_rrhh),

    areas,
    branches,
    _raw: apiRow,
  };
}

function toUiChatMsg(apiMsg, empleadoUserId) {
  const authorType = apiMsg.authorType;
  const sentByUserId = apiMsg.sentByUserId ?? apiMsg.enviado_por_usuario_id;
  const content = apiMsg.content ?? apiMsg.contenido ?? "";
  const sentAt = apiMsg.sentAt ?? apiMsg.fecha_envio;

  let from = "rrhh";

  if (authorType === "EMPLOYEE") from = "emp";
  else if (authorType === "RRHH") from = "rrhh";
  else if (empleadoUserId && sentByUserId === empleadoUserId) from = "emp";

  return {
    id: apiMsg.id,
    from,
    text: content,
    ts: fmtHoraAR(sentAt),
    _raw: {
      ...apiMsg,
      fecha_envio: sentAt,
    },
  };
}

function toUiGroupMsg(apiMsg, currentUserId) {
  const sentByUserId = Number(apiMsg.id_usuario_autor ?? apiMsg.sentByUserId);
  const content = apiMsg.contenido ?? apiMsg.content ?? "";
  const sentAt = apiMsg.fecha_envio ?? apiMsg.sentAt;

  const mine = currentUserId && sentByUserId === Number(currentUserId);

  return {
    id: apiMsg.id_mensaje ?? apiMsg.id,
    from: mine ? "rrhh" : "emp",
    text: content,
    ts: fmtHoraAR(sentAt),
    authorName: apiMsg.autor_nombre ?? "Empleado",
    _raw: {
      ...apiMsg,
      fecha_envio: sentAt,
    },
  };
}

export default function MensajesPage() {
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const temas = useMemo(() => ["General", "Novedades", "Eventos", "Capacitaciones", "Directivas", "Calendario Especial", "Otro"], []);
  const alcances = useMemo(() => ["Empresa", "Sucursal"], []);

  const fetchMensajes = useCallback(async (filters = {}) => {
    setLoadingMessages(true);
    try {
      const resp = await listarMensajes(filters);
      const list = Array.isArray(resp) ? resp : resp?.data || [];
      setMessages((Array.isArray(list) ? list : []).map(toUiMessage));
    } catch (e) {
      console.error(e);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);


  const [sucursales, setSucursales] = useState([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  const sucursalById = useMemo(() => {
    return new Map((sucursales || []).map((s) => [Number(s.id), s]));
  }, [sucursales]);


  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingSucursales(true);
      try {
        const resp = await listarSucursales();
        const list = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
        if (alive) setSucursales(list);
      } catch (e) {
        console.error(e);
        if (alive) setSucursales([]);
      } finally {
        if (alive) setLoadingSucursales(false);
      }
    })();
    return () => { alive = false; };
  }, []);



  // ===== CHAT: empleados + hilo =====
  const [chatEmpleados, setChatEmpleados] = useState([]);
  const [loadingChatEmpleados, setLoadingChatEmpleados] = useState(false);

  const [chatThread, setChatThread] = useState([]);
  const [loadingChatThread, setLoadingChatThread] = useState(false);
  const [sendingChat, setSendingChat] = useState(false);

  const [selectedHiloId, setSelectedHiloId] = useState(null);

  const [reclamosRaw, setReclamosRaw] = useState([]);
  const [loadingReclamos, setLoadingReclamos] = useState(false);

  // Map empleado_id -> {nombre, foto_url} usando lo que ya traés para Chat
  const empleadoById = useMemo(() => {
    return new Map((chatEmpleados || []).map((e) => [Number(e.empleado_id), e]));
  }, [chatEmpleados]);

  const fetchReclamos = useCallback(async () => {
    setLoadingReclamos(true);
    try {
      const resp = await listarReclamos({ limit: 200, offset: 0 });
      const list = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      setReclamosRaw(list);
    } catch (e) {
      console.error(e);
      setReclamosRaw([]);
    } finally {
      setLoadingReclamos(false);
    }
  }, []);

  useEffect(() => {
    fetchReclamos();
  }, [fetchReclamos]);

  const reclamos = useMemo(() => {
    return (reclamosRaw || []).map((apiRow) => {
      const emp = empleadoById.get(Number(apiRow.employeeId ?? apiRow.id_empleado));
      const suc = sucursalById.get(Number(apiRow.branchIdSnapshot ?? apiRow.id_sucursal_snapshot));

      const fallbackSucursalId = apiRow.branchIdSnapshot ?? apiRow.id_sucursal_snapshot;

      return {
        id: apiRow.id,

        empleado:
          apiRow.employeeFullName ??
          emp?.nombre ??
          `Empleado #${apiRow.employeeId ?? apiRow.id_empleado}`,

        avatar: emp?.foto_url ?? null,

        tipo: apiRow.type ?? apiRow.tipo,
        prioridad:
          PRIORIDAD_FROM_API[String(apiRow.priority ?? apiRow.prioridad)] ??
          String(apiRow.priority ?? apiRow.prioridad ?? "—"),
        estado:
          ESTADO_FROM_API[String(apiRow.state ?? apiRow.estado)] ??
          String(apiRow.state ?? apiRow.estado ?? "—"),

        fecha: fmtFechaAR(apiRow.createdAt ?? apiRow.fecha_creacion),

        sucursal:
          apiRow.branchNameSnapshot ??
          suc?.name ??
          suc?.nombre ??
          suc?.nombre_sucursal ??
          suc?.descripcion ??
          (fallbackSucursalId ? `Sucursal #${fallbackSucursalId}` : "—"),

        titulo: apiRow.title ?? apiRow.titulo ?? null,
        descripcion: apiRow.description ?? apiRow.descripcion ?? "",
        _raw: apiRow,
      };
    });
  }, [reclamosRaw, empleadoById, sucursalById]);

  const [savingReclamoEstado, setSavingReclamoEstado] = useState(false);

  const handleGuardarEstadoReclamo = useCallback(
    async (reclamoId, estadoNuevoApi) => {
      if (!reclamoId) return;

      setSavingReclamoEstado(true);
      try {
        await cambiarEstadoReclamo(reclamoId, {
          newState: estadoNuevoApi,
          comment: null,
        });

        setReclamosRaw((prev) =>
          (prev || []).map((r) =>
            r.id === reclamoId ? { ...r, state: estadoNuevoApi, estado: estadoNuevoApi } : r
          )
        );

        setReclamoModal({ open: false, mode: "detalle" });
      } catch (e) {
        console.error(e);
      } finally {
        setSavingReclamoEstado(false);
      }
    },
    []
  );

  const [savingReclamoRespuesta, setSavingReclamoRespuesta] = useState(false);

  const handleResponderReclamo = useCallback(
    async (reclamoId, mensaje) => {
      const txt = String(mensaje || "").trim();
      if (!reclamoId || !txt) return;

      setSavingReclamoRespuesta(true);
      try {
        await responderReclamo(reclamoId, {
          message: txt,
          visibleToEmployee: true,
        });

        setReclamosRaw((prev) =>
          (prev || []).map((r) =>
            r.id === reclamoId ? { ...r, state: "RESPONDIDO", estado: "RESPONDIDO" } : r
          )
        );

        setReclamoModal({ open: false, mode: "detalle" });
      } catch (e) {
        console.error(e);
      } finally {
        setSavingReclamoRespuesta(false);
      }
    },
    []
  );

  // ---------- STATE ----------
  const [mensajesView, setMensajesView] = useState("list"); // list | new | search

  const [msgSearch, setMsgSearch] = useState("");
  const [msgTema, setMsgTema] = useState("Todos");
  const [msgAlcance] = useState("Todos"); // (cuando sumes filtro UI, volvés a agregar el setter)
  const [msgTipo, setMsgTipo] = useState("Todos"); // Todos | Enviados | Recibidos (visual)

  const [selectedMsgId, setSelectedMsgId] = useState(null);

  const [chatSearch, setChatSearch] = useState("");
  const [chatArea, setChatArea] = useState("");
  const [selectedEmpleadoId, setSelectedEmpleadoId] = useState(null);

  const [chatMode, setChatMode] = useState("rrhh");

  const [groupSearch, setGroupSearch] = useState("");
  const [groupBranchId, setGroupBranchId] = useState("");
  const [groupAreaId, setGroupAreaId] = useState("");
  const [chatGroups, setChatGroups] = useState([]);
  const [loadingChatGroups, setLoadingChatGroups] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [groupThread, setGroupThread] = useState([]);
  const [loadingGroupThread, setLoadingGroupThread] = useState(false);
  const [sendingGroupChat, setSendingGroupChat] = useState(false);

  const [mensajeModalOpen, setMensajeModalOpen] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const selectedMsg = useMemo(() => {
    return messages.find((m) => m.id === selectedMsgId) || null;
  }, [messages, selectedMsgId]);

  const handleSelectMsg = (id) => {
    setSelectedMsgId(id);
    setMensajeModalOpen(true);
  };

  const selectedSucursalNombre = useMemo(() => {
    const id =
      selectedMsg?._raw?.branchId ??
      selectedMsg?._raw?.id_sucursal ??
      null;

    if (!id) return "";

    const s = sucursalById.get(Number(id));
    return s?.name || s?.nombre || s?.nombre_sucursal || s?.descripcion || `Sucursal #${id}`;
  }, [selectedMsg, sucursalById]);

  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);

  const selectedEmpleado = useMemo(
    () => chatEmpleados.find((e) => e.id === selectedEmpleadoId) || null,
    [chatEmpleados, selectedEmpleadoId]
  );

  const selectedGroup = useMemo(
    () => chatGroups.find((group) => String(group.id_grupo) === String(selectedGroupId)) || null,
    [chatGroups, selectedGroupId]
  );

  const currentUserId = useMemo(() => getCurrentUserIdFromStorage(), []);


  const handleSelectEmpleado = async (id) => {
    setSelectedEmpleadoId(id);
    setChatDrawerOpen(true);

    const emp = chatEmpleados.find((x) => x.id === id);
    if (!emp) return;

    setLoadingChatThread(true);
    setChatThread([]);
    setSelectedHiloId(null);

    try {
      let hiloId = emp.hilo_id;

      if (!hiloId) {
        const resp = await crearChatHilo({ employeeId: emp.employeeId });
        const hilo = resp?.data || resp;
        hiloId = hilo?.id;

        setChatEmpleados((prev) =>
          prev.map((x) =>
            x.id === emp.id
              ? {
                  ...x,
                  hilo_id: hiloId,
                  chat_estado: hilo?.state ?? x.chat_estado,
                }
              : x
          )
        );
      }

      if (!hiloId) throw new Error("No se pudo obtener/crear hilo");

      setSelectedHiloId(hiloId);

      const resp2 = await listarMensajesChat(hiloId);
      const list2 = Array.isArray(resp2?.data)
        ? resp2.data
        : Array.isArray(resp2)
        ? resp2
        : [];

      setChatThread(list2.map((m) => toUiChatMsg(m, emp.userId)));

      try {
        await marcarChatLeidoWeb(hiloId);
        setChatEmpleados((prev) =>
          prev.map((x) => (x.id === emp.id ? { ...x, unreadForRrhh: false } : x))
        );
      } catch (e) {
        console.error(e);
      }
    } catch (e) {
      console.error(e);
      setChatThread([]);
    } finally {
      setLoadingChatThread(false);
    }
  };


  const handleCloseChatDrawer = () => {
    setChatDrawerOpen(false);
    setSelectedEmpleadoId(null); // opcional, pero queda prolijo
  };

  const handleSendChat = async (text) => {
    const t = String(text || "").trim();
    if (!t) return;
    if (!selectedHiloId) return;

    setSendingChat(true);
    try {
      const resp = await enviarMensajeChat(selectedHiloId, { content: t });
      const msg = resp?.data || resp;

      setChatThread((prev) => [
        ...prev,
        toUiChatMsg(msg, selectedEmpleado?.userId),
      ]);
    } catch (e) {
      console.error(e);
    } finally {
      setSendingChat(false);
    }
  };

  const handleSelectGroup = async (id) => {
    setSelectedGroupId(id);
    setGroupDrawerOpen(true);
    setLoadingGroupThread(true);
    setGroupThread([]);

    try {
      const resp = await listarMensajesGrupoArea(id, {
        page: 1,
        pageSize: 100,
      });

      const list = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp)
        ? resp
        : [];

      setGroupThread(list.map((msg) => toUiGroupMsg(msg, currentUserId)));
    } catch (e) {
      console.error(e);
      setGroupThread([]);
    } finally {
      setLoadingGroupThread(false);
    }
  };

  const handleCloseGroupDrawer = () => {
    setGroupDrawerOpen(false);
    setSelectedGroupId(null);
  };

  const handleSendGroupChat = async (text) => {
    const t = String(text || "").trim();

    if (!t || !selectedGroupId) return;

    setSendingGroupChat(true);

    try {
      const resp = await enviarMensajeGrupoArea(selectedGroupId, {
        contenido: t,
      });

      const msg = resp?.data || resp;

      setGroupThread((prev) => [
        ...prev,
        toUiGroupMsg(msg, currentUserId),
      ]);

      setChatGroups((prev) =>
        prev.map((group) =>
          String(group.id_grupo) === String(selectedGroupId)
            ? {
                ...group,
                preview: msg.contenido ?? t,
                ultimo_mensaje_fecha: msg.fecha_envio ?? msg.last_message_at,
                ultimo_mensaje_autor: msg.autor_nombre ?? "RRHH",
                leido: true,
              }
            : group
        )
      );
    } catch (e) {
      console.error(e);
    } finally {
      setSendingGroupChat(false);
    }
  };


  const [reclamoSearch, setReclamoSearch] = useState("");
  const [selectedReclamoId, setSelectedReclamoId] = useState(null);
  const [reclamoModal, setReclamoModal] = useState({ open: false, mode: "detalle" });

  // Latest
  useEffect(() => {
    if (mensajesView !== "list") return;
    fetchMensajes({ limit: 50, offset: 0 });
  }, [mensajesView, refreshKey, fetchMensajes]);

  // Search (debounce)
  useEffect(() => {
    if (mensajesView !== "search") return;

    const hasSearch = Boolean(msgSearch.trim()) || (msgTema && msgTema !== "Todos");
    if (!hasSearch) return;

    const temaApi = msgTema && msgTema !== "Todos" ? (TEMA_TO_API[msgTema] || msgTema) : undefined;

    const t = setTimeout(() => {
      fetchMensajes({
        q: msgSearch?.trim() || undefined,
        tema: temaApi,
        limit: 50,
        offset: 0,
      });
    }, 250);

    return () => clearTimeout(t);
  }, [mensajesView, msgSearch, msgTema, fetchMensajes]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingChatEmpleados(true);
      try {
        const resp = await listarChatEmpleados({
          q: chatSearch?.trim() || undefined,
          limit: 200,
          offset: 0,
        });

        const list = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp)
          ? resp
          : [];

        if (alive) setChatEmpleados(list.map(toUiEmpleadoChat));
      } catch (e) {
        console.error(e);
        if (alive) setChatEmpleados([]);
      } finally {
        if (alive) setLoadingChatEmpleados(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [chatSearch]);

  useEffect(() => {
    if (chatMode !== "groups") return;

    let alive = true;

    (async () => {
      setLoadingChatGroups(true);

      try {
        const resp = await listarGruposAreaChat({
          q: groupSearch?.trim() || undefined,
          branchId: groupBranchId || undefined,
          areaId: groupAreaId || undefined,
          page: 1,
          pageSize: 200,
        });

        const list = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp)
          ? resp
          : [];

        if (alive) setChatGroups(list);
      } catch (e) {
        console.error(e);
        if (alive) setChatGroups([]);
      } finally {
        if (alive) setLoadingChatGroups(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [chatMode, groupSearch, groupBranchId, groupAreaId]);

  const areasDisponibles = useMemo(() => {
  const s = new Set();

  (chatEmpleados || []).forEach((e) => {
    if (Array.isArray(e.areas) && e.areas.length) {
      e.areas.forEach((a) => a && s.add(a));
      return;
    }

    if (e.area) {
      String(e.area)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
        .forEach((a) => s.add(a));
    }
  });

  return Array.from(s).sort((a, b) => a.localeCompare(b, "es"));
}, [chatEmpleados]);

  const handleCrearMensaje = async ({ tema, alcance, asunto, contenido, idSucursal }) => {
    const payload = {
      alcance: ALCANCE_TO_API[alcance] || alcance,   // EMPRESA | SUCURSAL
      id_sucursal: (alcance === "Sucursal" ? Number(idSucursal || 0) || null : null),
      tema: TEMA_TO_API[tema] || tema,
      titulo: asunto,
      contenido,
      activo: true,
    };

    const resp = await crearMensaje(payload);
    const created = resp?.data || resp;
    setRefreshKey((k) => k + 1);
    setMessages((prev) => [toUiMessage(created), ...prev]);

    setMensajesView("list");
  };




  const filteredMessages = useMemo(() => {
    return messages.filter((m) => {
      const q = msgSearch.trim().toLowerCase();
      const hitQ =
        !q ||
        m.titulo.toLowerCase().includes(q) ||
        m.preview.toLowerCase().includes(q) ||
        m.tema.toLowerCase().includes(q);

      const hitTema = !msgTema || msgTema === "Todos" || m.tema === msgTema;
      const hitAlc = !msgAlcance || msgAlcance === "Todos" || m.alcance === msgAlcance;

      return hitQ && hitTema && hitAlc;
    });
  }, [messages, msgSearch, msgTema, msgAlcance]);


  const filteredEmpleados = useMemo(() => {
    const q = chatSearch.trim().toLowerCase();

    return (chatEmpleados || []).filter((e) => {
      const nombre = String(e.nombre || "").toLowerCase();
      const dni = String(e?._raw?.dni || "").toLowerCase();
      const sucursal = String(e.sucursal || "").toLowerCase();
      const areaTexto = String(e.area || "").toLowerCase();

      const hitQ =
        !q ||
        nombre.includes(q) ||
        dni.includes(q) ||
        sucursal.includes(q) ||
        areaTexto.includes(q);

      const areasEmpleado = Array.isArray(e.areas)
        ? e.areas
        : String(e.area || "")
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);

      const hitArea = !chatArea || areasEmpleado.includes(chatArea);

      return hitQ && hitArea;
    });
  }, [chatEmpleados, chatSearch, chatArea]);


  const filteredReclamos = useMemo(() => {
    const q = reclamoSearch.trim().toLowerCase();
    return reclamos.filter((r) => {
      if (!q) return true;
      return (
        r.empleado.toLowerCase().includes(q) ||
        r.tipo.toLowerCase().includes(q) ||
        r.estado.toLowerCase().includes(q) ||
        r.sucursal.toLowerCase().includes(q)
      );
    });
  }, [reclamos, reclamoSearch]);

  const selectedReclamo = useMemo(
    () => reclamos.find((r) => r.id === selectedReclamoId) || null,
    [reclamos, selectedReclamoId]
  );

  const hasSearch = Boolean(msgSearch.trim()) || (msgTema && msgTema !== "Todos");

  const handleDeleteMessage = () => {
    if (selectedMsgId) {
      setDeleteError("");
      setDeleteMessageModalOpen(true);
      setDeletingMessageId(selectedMsgId);
    }
  };

  const handleConfirmDeleteMessage = async () => {
    if (!deletingMessageId) return;

    try {
      await eliminarMensaje(deletingMessageId);
      
      // Actualizar la lista de mensajes
      setMessages((prev) => prev.filter((m) => m.id !== deletingMessageId));
      
      // Cerrar modales
      setMensajeModalOpen(false);
      setDeleteMessageModalOpen(false);
      setSelectedMsgId(null);
      setDeletingMessageId(null);

    } catch (error) {
      console.error("Error al eliminar mensaje:", error);
      setDeleteError(
        error?.response?.data?.message ||
          error?.message ||
          "No se pudo eliminar el mensaje."
      );
    }
  };

  return (
    <Box sx={{ p: 2, overflowX: "hidden" }}>
      {/* Header */}
      <Box sx={{ position: "relative", mb: 2 }}>
        <IconButton
          onClick={() => navigate("/rrhh/dashboard")}
          sx={{
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: 40,
            height: 40,
            bgcolor: "secondary.500",
            "&:hover": { bgcolor: "secondary.600" },
          }}
        >
          <ArrowBackIcon sx={{ color: "common.white", fontSize: 30 }} />
        </IconButton>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <MainPill>Mensajería</MainPill>
        </Box>
      </Box>

      <Stack spacing={2}>
        {/* Row 1: Mensajes + Chat */}
        <Stack direction="row" spacing={2}>
          <OuterSurface sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <SectionPill>Mensajes</SectionPill>
            </Box>

            {/* Barra blanca con 2 botones */}
            <MensajesFilters view={mensajesView} setView={setMensajesView} />

            {/* Contenido según botón */}
            <Box sx={{ mt: 1 }}>
              {mensajesView === "list" && (
                <InnerPanel>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 999,
                        bgcolor: (t) => t.palette?.primary?.[500],
                        boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "common.white", lineHeight: 1 }}>
                        {loadingMessages ? "Cargando…" : "Ultimos Mensajes"}
                      </Typography>
                    </Box>
                  </Box>

                  <MensajesList
                    withSurface={false}
                    items={messages}
                    selectedId={selectedMsgId}
                    onSelect={handleSelectMsg}
                  />
                </InnerPanel>
              )}

              {mensajesView === "new" && (
                <InnerPanel>
                  <MensajeForm
                    variant="inline"
                    temas={temas}
                    alcances={alcances}
                    sucursales={sucursales}
                    loadingSucursales={loadingSucursales}
                    onClose={() => setMensajesView("list")}
                    onSubmit={handleCrearMensaje}
                  />
                </InnerPanel>
              )}

              {mensajesView === "search" && (
                <InnerPanel>
                  <MensajesSearchPanel
                    temas={temas}
                    tema={msgTema}
                    setTema={setMsgTema}
                    search={msgSearch}
                    setSearch={setMsgSearch}
                    tipo={msgTipo}
                    setTipo={setMsgTipo}
                  >
                    {hasSearch ? (
                      <MensajesList
                        withSurface={false}
                        items={filteredMessages}
                        selectedId={selectedMsgId}
                        onSelect={handleSelectMsg}
                      />
                    ) : null}
                  </MensajesSearchPanel>
                </InnerPanel>
              )}
            </Box>
          </OuterSurface>

          {/* Chat RRHH y Groups */}
          <OuterSurface sx={{ flex: 1, minWidth: 320 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <SectionPill sx={{ fontSize: 18, px: 3, py: 0.9 }}>Chats</SectionPill>
            </Box>

            <ChatModeSwitch value={chatMode} onChange={setChatMode} />

            {chatMode === "rrhh" ? (
              <ChatSidebarEmpleados
                empleados={filteredEmpleados}
                areasDisponibles={areasDisponibles}
                loading={loadingChatEmpleados}
                selectedId={selectedEmpleadoId}
                onSelect={handleSelectEmpleado}
                search={chatSearch}
                setSearch={setChatSearch}
                area={chatArea}
                setArea={setChatArea}
              />
            ) : (
              <ChatSidebarGrupos
                grupos={chatGroups}
                loading={loadingChatGroups}
                selectedId={selectedGroupId}
                onSelect={handleSelectGroup}
                search={groupSearch}
                setSearch={setGroupSearch}
                branchId={groupBranchId}
                setBranchId={setGroupBranchId}
                areaId={groupAreaId}
                setAreaId={setGroupAreaId}
              />
            )}
          </OuterSurface>
        </Stack>

        {/* Row 2: Reclamos (dejamos como estaba, solo sacá el "s" suelto) */}
        <OuterSurface>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <SectionPill>Reclamos</SectionPill>
          </Box>

          <ReclamosFilters
            search={reclamoSearch}
            setSearch={setReclamoSearch}
            hasSelection={Boolean(selectedReclamoId)}
            onOpenDetalle={() => setReclamoModal({ open: true, mode: "detalle" })}
            onOpenEstado={() => setReclamoModal({ open: true, mode: "estado" })}
            onOpenResponder={() => setReclamoModal({ open: true, mode: "responder" })}
            onViewFull={() => setReclamoModal({ open: true, mode: "full" })}
          />
          {loadingReclamos ? (
            <Typography
              sx={{
                mt: 1,
                fontSize: 12,
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              Cargando reclamos…
            </Typography>
          ) : null}

          <Box sx={{ mt: 2 }}>
            <ReclamosTable rows={filteredReclamos} selectedId={selectedReclamoId} onSelect={setSelectedReclamoId} />
          </Box>

          <ReclamoDetailModal
            open={reclamoModal.open}
            mode={reclamoModal.mode}
            reclamo={selectedReclamo}
            onClose={() => setReclamoModal({ open: false, mode: "detalle" })}
            onSubmitEstado={(nuevoEstado) => handleGuardarEstadoReclamo(selectedReclamoId, nuevoEstado)}
            savingEstado={savingReclamoEstado}
            onSubmitRespuesta={(msg) => handleResponderReclamo(selectedReclamoId, msg)} 
            savingRespuesta={savingReclamoRespuesta}
          />
        </OuterSurface>
      </Stack>

      <ChatThreadDrawer
        open={chatDrawerOpen}
        onClose={handleCloseChatDrawer}
        mode="rrhh"
        empleado={selectedEmpleado}
        thread={chatThread}
        loading={loadingChatThread}
        sending={sendingChat}
        onSend={handleSendChat}
      />

      <ChatThreadDrawer
        open={groupDrawerOpen}
        onClose={handleCloseGroupDrawer}
        mode="group"
        grupo={selectedGroup}
        thread={groupThread}
        loading={loadingGroupThread}
        sending={sendingGroupChat}
        onSend={handleSendGroupChat}
      />

      <MensajeDetailModal
        open={mensajeModalOpen}
        onClose={() => setMensajeModalOpen(false)}
        message={selectedMsg}
        sucursalNombre={selectedSucursalNombre}
        onDelete={handleDeleteMessage}
      />

      <DeleteMessageModal
        open={deleteMessageModalOpen}
        onClose={() => {
          setDeleteMessageModalOpen(false);
          setDeletingMessageId(null);
        }}
        onConfirm={handleConfirmDeleteMessage}
        messageTitle={selectedMsg?.titulo ?? "Mensaje"}
      />

      <Snackbar
        open={Boolean(deleteError)}
        autoHideDuration={5000}
        onClose={() => setDeleteError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setDeleteError("")}
          sx={{ width: "100%" }}
        >
          {deleteError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
